import React from 'react';

import { Card, Group, Stack, Text, ThemeIcon, useMantineTheme } from '@mantine/core';
import {
  IconCircleCheck,
  IconCircleDashed,
  IconPackage,
  IconSettings,
  IconTags,
  IconToggleLeft,
  IconUserCircle,
} from '@tabler/icons-react';
import { useStyles } from 'pages/private/owner/FlowBuilder/styles';

// Card-type keys drive the icon. They're plain strings so both the Kilo→GLM and
// GLM→Kilo builders can use their own vocabularies.
export type GLMFlowBuilderCardType = string;

export interface GLMFlowBuilderCardProps {
  cardType: GLMFlowBuilderCardType;
  headerLabel: string;
  /** Pre-resolved display text (callers resolve ids→names before passing this). */
  value?: string | null;
  placeholderText?: string;
  isSelected?: boolean;
}

const getIcon = (cardType: string): React.ReactNode => {
  switch (cardType) {
    case 'package':
      return <IconPackage size={18} />;
    case 'status':
      return <IconCircleDashed size={18} />;
    case 'action':
      return <IconUserCircle size={18} />;
    case 'tag':
      return <IconTags size={18} />;
    case 'enabled':
      return <IconToggleLeft size={18} />;
    case 'trigger':
      return <IconCircleDashed size={18} />;
    default:
      return <IconSettings size={18} />;
  }
};

const GLMFlowBuilderCard = ({
  cardType,
  headerLabel,
  value,
  placeholderText,
  isSelected = false,
}: GLMFlowBuilderCardProps): JSX.Element => {
  const { classes } = useStyles();
  const theme = useMantineTheme();

  const displayValue = value;
  const isFilled = !!displayValue;

  const cardClass = isSelected
    ? classes.selectedCard
    : isFilled
    ? classes.displayCard
    : classes.emptyDisplayCard;
  const iconColor = isFilled || isSelected ? 'blue' : 'gray';

  return (
    <Stack spacing='xs' align='flex-end' style={{ width: '90%' }}>
      <Group spacing={4} align='center' style={{ alignSelf: 'flex-start' }}>
        <Text color='dimmed' style={{ fontSize: '12px' }}>
          {headerLabel}
        </Text>
      </Group>
      <Group position='center' style={{ width: '100%' }}>
        <Card
          className={cardClass}
          shadow='sm'
          padding='md'
          radius='md'
          withBorder
          style={{ width: '100%' }}
        >
          <Group position='apart' style={{ width: '100%' }}>
            <ThemeIcon color={iconColor} variant='light' size='lg' radius='md'>
              {getIcon(cardType)}
            </ThemeIcon>
            <Text
              style={{ flex: 1, paddingLeft: 12, fontStyle: isFilled ? 'normal' : 'italic' }}
              color={isFilled ? undefined : 'dimmed'}
            >
              {displayValue || placeholderText || 'Setting this up...'}
            </Text>
            {isFilled && <IconCircleCheck size={20} color={theme.colors.kiloBlue[5]} />}
          </Group>
        </Card>
      </Group>
    </Stack>
  );
};

export default GLMFlowBuilderCard;
